import { isEmpty } from 'utilities/helperFunctions';

require('./RAProjectionChart.scss');

//@ vendors
const React = require('react');
const PropTypes = require('prop-types');
const classNames = require('classnames');
const {
    ComposedChart,
    ResponsiveContainer,
    Line,
    Area,
    YAxis,
    XAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ReferenceDot,
} = require('recharts');
// @ components
const RACustomTickWithObjective = require('./RACustomTickWithObjective/RACustomTickWithObjective');
const RACustomizedCursorLine = require('components/RAComponents/RACustomizedCursorLine/RACustomizedCursorLine');
const RAOneLineSymbolAmount = require('components/RAComponents/commons/RAOneLineSymbolAmount/RAOneLineSymbolAmount');
const RAProjectionChartCustomTick = require('./RAProjectionChartCustomTick');
const RAProjectionChartTooltip = require('./RAProjectionChartTooltip');
// @ utilities
import { FormattedText } from 'core/i18n';
const { formatAmount } = require('utilities/RACurrencyHelper');
const { getShowPointsAndDayTerm } = require('utilities/RAObjectiveTrackingGetYearAndNoOfPoints');
// @ constants
const { EURO_SYMBOL, EURO_TEXT } = require('constants/RAConstants');
const {
    AXIS_LINE_COLOR,
    LINE_STROKE_COLOR,
    OBJECTIVE_LABEL,
    OPACITY_HIDE,
    OPACITY_SHOW,
    REF_DOT_FILL_COLOR,
    REF_DOT_STROKE_COLOR,
    TODAY_LABEL,
} = require('constants/RAConstants/RAObjectiveTracking');
const { DATA } = require('./constants');

class RAProjectionChart extends React.Component {
    constructor(props) {
        super(props);

        this.getActiveDot = this.getActiveDot.bind(this);
        this.getLineShape = this.getLineShape.bind(this);
        this.getReferenceDot = this.getReferenceDot.bind(this);
        this.getYMaxDataDomain = this.getYMaxDataDomain.bind(this);
        this.getYMinDataDomain = this.getYMinDataDomain.bind(this);
        this.handleActiveDotClick = this.handleActiveDotClick.bind(this);
        this.handleObjectiveClick = this.handleObjectiveClick.bind(this);
        this.handleResetCustomToolTip = this.handleResetCustomToolTip.bind(this);
        this.updateTooltip = this.updateTooltip.bind(this);

        this.state = {
            strokeWidth: 0,
            disableChartTooltip: false,
            buttonToggle: '',
        };

        this.area = null;
        this.tooltip = null;
        this.chartP = null;
        this.point = {};
    }

    handleActiveDotClick() {
        if (this.chartP.state.activeLabel === 0) {
            this.setState({
                buttonToggle: TODAY_LABEL,
                strokeWidth: 1,
            });
        }
    }

    handleObjectiveClick() {
        this.setState({
            strokeWidth: 1,
            disableChartTooltip: true,
            buttonToggle: OBJECTIVE_LABEL,
        });
        this.tooltip.style.opacity = OPACITY_SHOW;
        this.updateTooltip();
    }

    handleResetCustomToolTip(chart) {
        const { data, disabled } = this.props;
        const currentData = disabled || isEmpty(data) ? DATA : data;
        const noOfChartPoints = currentData.length;

        if (this.state.disableChartTooltip && (chart.activeLabel ?? 0) !== noOfChartPoints - 1) {
            this.setState({
                disableChartTooltip: false,
            });
            this.tooltip.style.opacity = OPACITY_HIDE;
        }
    }

    getTooltip() {
        let tooltip = <span />;
        const disableChartTooltip = this.state.disableChartTooltip;

        if (!this.props.disabled && !disableChartTooltip) {
            tooltip = (
                <Tooltip
                    active={disableChartTooltip}
                    content={<RAProjectionChartTooltip active="false" />}
                    cursor={<RACustomizedCursorLine />}
                />
            );
        }

        return tooltip;
    }

    getReferenceLineXValue(noOfChartPoints) {
        let referenceLineXValue = 0;

        if (this.state.buttonToggle === OBJECTIVE_LABEL) {
            referenceLineXValue = noOfChartPoints - 1;
        }

        return referenceLineXValue;
    }

    getYMinDataDomain(yMinData) {
        const { objAmount } = this.props;

        if (yMinData > objAmount && objAmount !== 0) {
            return objAmount;
        }

        return yMinData;
    }

    getYMaxDataDomain(yMaxData) {
        const { objAmount } = this.props;

        if (yMaxData > objAmount) {
            return yMaxData;
        }

        return objAmount;
    }

    getCustomTickElement(noOfChartPoints) {
        const { disabled, isObjectiveTracking, tickInfo, years } = this.props;
        let customTickElement = null;

        if (isObjectiveTracking && !disabled) {
            const showPointsList = getShowPointsAndDayTerm(noOfChartPoints);

            customTickElement = (
                <RACustomTickWithObjective
                    activeButtonLabel={this.state.buttonToggle}
                    disabled={disabled}
                    noOfChartPoints={noOfChartPoints}
                    onObjectiveClick={this.handleObjectiveClick}
                    showPointsList={showPointsList}
                    tickInfo={tickInfo}
                />
            );
        } else {
            customTickElement = (
                <RAProjectionChartCustomTick
                    disabled={disabled}
                    onObjectiveClick={this.handleObjectiveClick}
                    years={years}
                />
            );
        }

        return customTickElement;
    }

    getActiveDot({ cx, cy }) {
        return (
            <g>
                <circle
                    onClick={this.handleActiveDotClick}
                    cx={cx}
                    cy={cy}
                    strokeWidth="2"
                    r="8"
                    opacity="1"
                    stroke={'false'}
                    fill="white"
                    cursor="pointer"
                />
            </g>
        );
    }

    getLineShape({ x1, y1, x2, y2, x }) {
        const { disabled, isObjectiveTracking } = this.props;
        let shape;

        if (isObjectiveTracking && !disabled) {
            shape = (
                <g>
                    <defs>
                        <marker
                            id="arrow"
                            markerWidth="20"
                            markerHeight="20"
                            refX="0"
                            refY="3"
                            orient="-90deg"
                            markerUnits="strokeWidth"
                            viewBox="0 0 20 20"
                        >
                            <path d="M0,0 L0,6 L9,3 z" fill={LINE_STROKE_COLOR} />
                        </marker>
                    </defs>
                    <line
                        x={x}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={LINE_STROKE_COLOR}
                        strokeWidth={this.state.strokeWidth}
                        markerStart="url(#arrow)"
                        strokeDasharray="2"
                        fill="none"
                        fillOpacity="1"
                    />
                </g>
            );
        }

        return shape;
    }

    getReferenceDot({ cx, cy }) {
        const { isObjectiveTracking, smallObjectiveDot, disabled } = this.props;
        let shape;
        let strokeWidth = '5';
        let radius = '15';

        if (smallObjectiveDot) {
            strokeWidth = '3';
            radius = '10';
        }

        if (isObjectiveTracking && !disabled) {
            shape = (
                <g>
                    <defs>
                        <filter
                            id="dropshadow"
                            x="-40%"
                            y="-40%"
                            width="180%"
                            height="180%"
                            filterUnits="userSpaceOnUse"
                        >
                            <feGaussianBlur in="SourceAlpha" stdDeviation="1" result="blur" />
                            <feOffset dx="0" dy="1" in="blur" result="offset" />
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.5" in="offset" result="transfer" />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <circle
                        ref={ref => (this.area = ref)} // eslint-disable-line
                        onClick={this.handleObjectiveClick}
                        cx={cx}
                        cy={cy}
                        strokeWidth={strokeWidth}
                        r={radius}
                        stroke={REF_DOT_STROKE_COLOR}
                        fill={REF_DOT_FILL_COLOR}
                        cursor="pointer"
                        style={{ filter: 'url(#dropshadow)' }}
                    />
                </g>
            );
        }

        return shape;
    }

    moneyFormatter(tick) {
        let parsed = '';

        if (tick) {
            const amount = formatAmount(tick, null, 0);

            parsed = `${amount} ${EURO_SYMBOL}`;
        }

        return parsed;
    }

    updateTooltip() {
        const centerXValue = this.area.getAttribute('cx');
        const centerYValue = this.area.getAttribute('cy');
        const tooltipPositionX = Math.round(centerXValue) - 150;
        const tooltipPositionY = Math.round(centerYValue) - 50;

        this.tooltip.style.transform = `translate(${tooltipPositionX}px, ${tooltipPositionY}px)`;
    }

    render() {
        const {
            data,
            loading,
            disabled,
            small,
            syncId,
            margin,
            yDomain,
            yTickMargin,
            yTickFormatter,
            yWidth,
            objAmount,
            actualAmount,
        } = this.props;
        const currentClassName = classNames(
            'ra-projection-chart',
            {
                'ra-projection-chart--loading': loading,
                'ra-projection-chart--disabled': disabled,
            },
            small ? 'ra-projection-chart--small' : 'ra-projection-chart--medium'
        );
        const currentData = disabled || isEmpty(data) ? DATA : data;
        const noOfChartPoints = currentData.length;
        const tooltip = this.getTooltip();
        const yAxisDomain = yDomain || [this.getYMinDataDomain, this.getYMaxDataDomain];
        const customTickElement = this.getCustomTickElement(noOfChartPoints);
        const referenceLineXValue = this.getReferenceLineXValue(noOfChartPoints);
        const remainingAmount = objAmount - actualAmount;
        const yTickMarginAlternative = screen.width > 480 ? 10 : 20;

        return (
            <div className={currentClassName}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={currentData}
                        margin={margin || { top: 40, right: 10 }}
                        onMouseMove={this.handleResetCustomToolTip}
                        ref={ref => (this.chartP = ref)} // eslint-disable-line
                        syncId={syncId}
                    >
                        {tooltip}
                        <CartesianGrid vertical={false} strokeDasharray="2" />
                        <YAxis
                            axisLine={false}
                            domain={yAxisDomain}
                            scale="linear"
                            tickFormatter={yTickFormatter || this.moneyFormatter}
                            tickLine={false}
                            tickMargin={yTickMargin || yTickMarginAlternative}
                            width={yWidth || 100}
                        />
                        <XAxis
                            axisLine={{ stroke: AXIS_LINE_COLOR, strokeWidth: 1 }}
                            domain={[0, 'dataMax']}
                            interval={0}
                            padding={{ left: 0, right: 25 }}
                            scale="point"
                            tick={customTickElement}
                            tickLine={false}
                            tickMargin={10}
                        />
                        <Area
                            activeDot={false}
                            className="ra-projection-chart__area--grey"
                            dataKey="lowRange"
                            dot={false}
                            type="monotone"
                        />
                        <Area
                            activeDot={false}
                            className="ra-projection-chart__area--light-blue"
                            dataKey="highRange"
                            dot={false}
                            type="monotone"
                        />
                        <Line
                            activeDot={this.getActiveDot}
                            className="ra-projection-chart__line--grey"
                            dataKey="low"
                            dot={{ opacity: 0 }}
                            isAnimationActive={false}
                            type="monotone"
                        />
                        <Line
                            activeDot={this.getActiveDot}
                            className="ra-projection-chart__line--light-blue"
                            dataKey="up"
                            dot={{ opacity: 0 }}
                            isAnimationActive={false}
                            type="monotone"
                        />
                        <Line
                            activeDot={this.getActiveDot}
                            className="ra-projection-chart__line--default"
                            dataKey="mid"
                            dot={{ r: 6 }}
                            isAnimationActive={false}
                            type="monotone"
                        />
                        <ReferenceLine x={referenceLineXValue} shape={this.getLineShape} />
                        <ReferenceDot x={noOfChartPoints - 1} y={objAmount} shape={this.getReferenceDot} />
                    </ComposedChart>
                </ResponsiveContainer>
                <div
                    className="ra-projection-chart__custom-tooltip"
                    ref={ref => (this.tooltip = ref)} // eslint-disable-line
                >
                    <div className="ra-projection-chart__custom-tooltip-content">
                        <div className="ra-projection-chart__custom-tooltip-title">
                            <FormattedText value="raObjectiveTracking-objectiveText" />
                        </div>

                        <span className="ra-projection-chart__custom-tooltip-balance">
                            <RAOneLineSymbolAmount amountObject={{ amount: objAmount, currency: EURO_TEXT }} />
                        </span>
                        <div className="ra-projection-chart__custom-tooltip-title">
                            <FormattedText value="raObjectiveTracking-subtractText" />
                        </div>
                        <span className="ra-projection-chart__custom-tooltip-term">
                            <RAOneLineSymbolAmount amountObject={{ amount: remainingAmount, currency: EURO_TEXT }} />
                        </span>
                    </div>
                </div>
            </div>
        );
    }
}

RAProjectionChart.propTypes = {
    actualAmount: PropTypes.number,
    data: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    height: PropTypes.number,
    isObjectiveTracking: PropTypes.bool,
    loading: PropTypes.bool,
    margin: PropTypes.object,
    objAmount: PropTypes.number,
    small: PropTypes.bool,
    smallObjectiveDot: PropTypes.bool,
    syncId: PropTypes.string,
    tickInfo: PropTypes.object,
    yDomain: PropTypes.array,
    yTickFormatter: PropTypes.func,
    yTickMargin: PropTypes.number,
    yWidth: PropTypes.number,
    years: PropTypes.number.isRequired,
};

RAProjectionChart.defaultProps = {
    isObjectiveTracking: false,
    objAmount: 0,
    smallObjectiveDot: false,
};

module.exports = RAProjectionChart;
